/**
 * ActivityRail — persistent right-side rail showing activity feed.
 *
 * Replaces FloatingTransactionPanel. Resizable via left-edge drag handle.
 * Width persisted to localStorage, clamped 200–400px.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Money } from '@/components/atoms/Money';
import { useActivity } from '@/contexts/ActivityContext';
import {
  deriveConnectionStatus,
  formatTime,
  statusDot,
  statusLabel,
  eventTypeLabel,
  eventTypeColor,
} from '@/lib/activityHelpers';

const WIDTH_KEY = 'activity-rail-width';
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;
const DEFAULT_WIDTH = 280;

function readPersistedWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    if (raw) {
      const n = parseInt(raw, 10);
      if (n >= MIN_WIDTH && n <= MAX_WIDTH) return n;
    }
  } catch {
    /* noop */
  }
  return DEFAULT_WIDTH;
}

export function ActivityRail() {
  const { events } = useActivity();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(readPersistedWidth);
  const [isResizing, setIsResizing] = useState(false);
  /** When true, auto-scroll is active (user hasn't scrolled up). */
  const autoScrollRef = useRef(true);

  // Filter out noisy queue_velocity events
  const txEvents = events.filter((e) => e.type !== 'queue_velocity');
  const connectionStatus = deriveConnectionStatus(events);

  // Auto-scroll to top on new events (events are newest-first)
  useEffect(() => {
    if (autoScrollRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [txEvents.length]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // If user scrolled away from top, disable auto-scroll
    autoScrollRef.current = el.scrollTop < 10;
  }, []);

  // Resize drag handling
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = widthRef.current;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Dragging left increases width, dragging right decreases
      const delta = startX - moveEvent.clientX;
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
      setWidth(next);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Persist width on change (debounced by not persisting during drag)
  useEffect(() => {
    if (!isResizing) {
      try {
        localStorage.setItem(WIDTH_KEY, String(width));
      } catch {
        /* noop */
      }
    }
  }, [width, isResizing]);

  return (
    <div
      className="bg-muted border-border/50 relative flex flex-shrink-0 flex-col border-l"
      style={{ width: `${width}px` }}
    >
      {/* Resize handle */}
      <div
        className={cn(
          'absolute inset-y-0 left-0 w-1 cursor-col-resize transition-colors',
          isResizing ? 'bg-primary/50' : 'hover:bg-primary/30'
        )}
        onMouseDown={handleMouseDown}
      />

      {/* Connection status header */}
      <div className="border-border/50 flex items-center gap-2 border-b px-3 py-2">
        <div className={cn('h-2 w-2 rounded-full', statusDot[connectionStatus])} />
        <span className="text-muted-foreground text-xs">{statusLabel[connectionStatus]}</span>
        <span className="text-muted-foreground ml-auto text-xs tabular-nums">
          {txEvents.length}
        </span>
      </div>

      {/* Scrollable event list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto"
        onScroll={handleScroll}
      >
        {txEvents.length === 0 ? (
          <div className="text-muted-foreground px-3 py-8 text-center text-xs">No activity yet</div>
        ) : (
          txEvents.map((event) => (
            <div
              key={event.id}
              className="border-border/20 flex items-start gap-2 border-b px-3 py-1.5 text-xs"
            >
              <span
                className={cn(
                  'mt-0.5 flex-shrink-0 font-mono text-[10px] uppercase',
                  eventTypeColor(event.type)
                )}
              >
                {eventTypeLabel(event.type)}
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-foreground font-medium">{event.message}</span>
                {event.amount !== undefined && (
                  <>
                    <span className="text-muted-foreground mx-1">·</span>
                    <Money
                      value={event.amount}
                      className="text-xs"
                    />
                  </>
                )}
              </div>
              <span className="text-muted-foreground mt-0.5 flex-shrink-0 whitespace-nowrap text-[10px]">
                {formatTime(event.timestamp)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
